import { ApplicationRef, Component, NgZone, OnDestroy, OnInit } from '@angular/core';
import { RouterModule, Router, NavigationEnd } from '@angular/router';
import { Subscription } from 'rxjs';
import { filter } from 'rxjs/operators';
import { CommonModule } from '@angular/common';
import { AppLayoutComponent } from './layout/app-layout.component';
import { ThemeService } from './services/theme.service';
import { GlobalErrorService } from './services/global-error.service';
import { TimezoneService } from './services/timezone.service';
import { ProfileService } from './services/profile.service';
import { VersionCheckService } from './services/version-check.service';
import { NotificationService } from './services/notification.service';
import { ReportGenerationService } from './features/reports/services/report-generation.service';
// ErrorBannerComponent is no longer eagerly imported — it lives behind an
// @defer (on idle) block below so its ~3 kB module + Angular animations infra
// stay out of the initial bundle (SCRUM-813).
import { ErrorBannerComponent } from './components/error-banner.component';
import { NotificationHostComponent } from './layout/notification-host.component';
import { UpdateAvailableBannerComponent } from './components/update-available-banner.component';
// SCRUM-1971 — global passkey-adoption banner (post-login nudge + cross-device offer).
import { PasskeyPromptComponent } from './components/passkey-prompt.component';
import { CarModeSizeComponent } from './shared/car-mode-size.component';

/** RDS-16 — the car-native glance routes render outside the desktop shell. */
export function isCarPath(url: string): boolean {
  return /^\/car(\/|\?|#|$)/.test(url);
}

@Component({
    selector: 'app-root',
    imports: [
        CommonModule,
        RouterModule,
        AppLayoutComponent,
        // ErrorBannerComponent is referenced inside the @defer block so it
        // becomes a separate chunk; including it in `imports` is required
        // for the template type-check + the deferred loader to find it.
        ErrorBannerComponent,
        NotificationHostComponent,
        // SCRUM-1961 — "new version available" prompt (replaces auto-reload).
        UpdateAvailableBannerComponent,
        // SCRUM-1971 — passkey-adoption banner.
        PasskeyPromptComponent,
        // SCRUM-2261 — in-car A-/A+ text-size control (renders only in car mode).
        CarModeSizeComponent,
    ],
    // Need to import diagnostics for standalone routing
    // It will be lazy loaded via the route
    template: `
    <div class="app-root" [ngClass]="{ 'dark': isDarkMode }">
      <!-- Global Error UI — deferred so the banner + animation triggers don't
           inflate the initial bundle. The error service still buffers any
           errors that fire before the banner hydrates. -->
      @defer (on idle) {
        <app-error-banner></app-error-banner>
      }

      <!-- SCRUM-1627 — global in-app toast host (visible from any page, survives navigation) -->
      <app-notification-host></app-notification-host>

      <!-- SCRUM-1961 — non-blocking "new version available" prompt; the app no
           longer hard-reloads mid-read, it waits for the user to tap Refresh. -->
      <app-update-available-banner></app-update-available-banner>

      <!-- SCRUM-1971 — passkey-adoption banner: post-password enrolment nudge
           and cross-device "add a passkey to this device" offer. Mounted at the
           shell so it survives the post-login navigation and shows over any page. -->
      <app-passkey-prompt></app-passkey-prompt>

      <!-- SCRUM-2261 — in-car text-size control; self-hides unless car mode is on. -->
      <app-car-mode-size [carNative]="isCarRoute"></app-car-mode-size>

      <!-- App Layout. BRIEF_ANALYSIS_REDESIGN_P1_SPEC RDS-16 — the car-native
           screens (/car, /car/why, /car/menu, /car/ticker/:sym) are full-screen
           glance views with their own top and bottom bars, so they render
           without the desktop topbar / sidebar (every target there is ≥72px). -->
      <app-layout *ngIf="!isAuthPage && !isCarRoute"></app-layout>
      <router-outlet *ngIf="!isAuthPage && isCarRoute"></router-outlet>
      <router-outlet *ngIf="isAuthPage"></router-outlet>
      <router-outlet *ngIf="isDiagnostics"></router-outlet>
    </div>
  `,
    styles: [`
    :host {
      display: block;
      width: 100%;
      height: 100dvh;
      overflow: hidden;
    }

    .app-root {
      width: 100%;
      height: 100%;
      display: flex;
      flex-direction: column;
      background: var(--surface-color);
      color: var(--text-color);
      transition: background-color 0.3s ease, color 0.3s ease;
    }

    .app-root.dark {
      color-scheme: dark;
    }
  `]
})
export class AppComponent implements OnInit, OnDestroy {
  title = 'AlphaGroves';
  isAuthPage = false;
  isDiagnostics = false;
  isDarkMode = false;
  /** RDS-16 — on a car-native route (/car, /car/why, /car/menu, /car/ticker/:sym). */
  isCarRoute = false;

  // App-shell wake handler (Honman 2026-07-17). Per-page wake handlers on
  // analysis-view were not enough — iPad Safari suspend/resume was also
  // leaving the sidebar in a state where menu taps looked pressed but did
  // not navigate. Running ApplicationRef.tick() from the app root on the
  // two wake events forces a synchronous change-detection tick across the
  // ENTIRE tree, which unsticks any pending navigation or component state
  // that the zone missed.
  private readonly wakeHandler = () => {
    if (typeof document === 'undefined') return;
    this.zone.run(() => {
      try { this.appRef.tick(); } catch { /* app not yet booted */ }
    });
  };
  private readonly visibilityWake = () => {
    if (document.visibilityState === 'visible') this.wakeHandler();
  };
  private readonly pageshowWake = () => this.wakeHandler();

  // SCRUM-1943 — global report-ready toast. ReportGenerationService.settled$
  // fires when a report finishes generating even after the user has left the
  // page; the app-shell raises a clickable "<TICKER> report ready" toast so
  // they can jump straight back to it.
  private reportReadySub?: Subscription;

  constructor(
    private readonly router: Router,
    private readonly themeService: ThemeService,
    private readonly globalErrorService: GlobalErrorService,
    private readonly timezoneService: TimezoneService,
    private readonly profileService: ProfileService,
    private readonly appRef: ApplicationRef,
    private readonly zone: NgZone,
    private readonly versionCheck: VersionCheckService,
    private readonly notify: NotificationService,
    private readonly reportGen: ReportGenerationService,
  ) {
    // Set synchronously from the browser URL so app-layout never mounts on
    // auth/reset/forgot routes — prevents TopbarComponent from firing
    // authenticated API calls (loadAvatar etc.) before the user is logged in,
    // which would trigger the 401 → login redirect in the interceptors.
    //
    // SCRUM-822 (2026-06-07): ALSO treat the user as on an auth page when
    // there's no valid token. Without this, hitting a protected URL like /
    // briefly flashed the app-layout (with the dashboard shell) before the
    // route's authGuard could redirect to /auth/login. The flash is gone now
    // because we know synchronously, from localStorage, that the guard is
    // about to redirect — so we hide the layout up-front.
    const path = window.location.pathname; // NOSONAR S7764 — browser-only Angular code
    const isExplicitAuthRoute = path.includes('/auth') || path.includes('/login') || path.includes('/register');
    const accessToken = localStorage.getItem('hub_access_token');
    const tokenExpiresIn = localStorage.getItem('hub_token_expires_in');
    const tokenIsValid = !!accessToken &&
      (!tokenExpiresIn || Date.now() < Number.parseInt(tokenExpiresIn, 10));
    this.isAuthPage = isExplicitAuthRoute || !tokenIsValid;
    this.isDiagnostics = path.includes('/diagnostics');
    this.isCarRoute = isCarPath(path);
  }

  ngOnInit(): void {
    // Only update on NavigationEnd — router.url is stale during early events
    // (NavigationStart etc.) which would flip isAuthPage back to false.
    // Capture the build fingerprint this tab started on, then on every route
    // change check whether a newer build has been deployed and, if so, reload
    // to it — so users stop running a stale app after a deploy (no manual
    // hard-refresh, and no "page doesn't load until I click" from an old bundle).
    void this.versionCheck.init();

    // Car-Native UI (CAR_MODE_REDESIGN_EPIC) — in car mode, send the landing
    // page to the glanceable /car Home instead of the dense desktop dashboard.
    // One-shot, only from the dashboard root, only when the `tesla` class is on
    // <html> (car-mode detect in main.ts). Deep links + every other route are
    // untouched, and desktop never redirects.
    try {
      const isCar = typeof document !== 'undefined'
        && document.documentElement.classList.contains('tesla');
      const atDashboardRoot = this.router.url === '/' || this.router.url.startsWith('/dashboard');
      if (isCar && atDashboardRoot && !this.router.url.startsWith('/car')) {
        void this.router.navigate(['/car']);
      }
    } catch { /* SSR / no document — skip */ }

    this.router.events.pipe(filter(e => e instanceof NavigationEnd)).subscribe(() => {
      this.isAuthPage = this.router.url.includes('/auth') || this.router.url.includes('/login') || this.router.url.includes('/register');
      this.isDiagnostics = this.router.url.includes('/diagnostics');
      this.isCarRoute = isCarPath(this.router.url);
      void this.versionCheck.reloadIfStale();
    });

    // Subscribe to theme changes
    this.themeService.getTheme().subscribe((theme) => {
      this.isDarkMode = theme === 'dark';
    });

    // SCRUM-1489 — hydrate TimezoneService from the authoritative backend
    // profile so every | tzDate render uses the user's chosen IANA zone.
    // TimezoneService seeds from localStorage / browser tz synchronously in
    // its ctor, so this is a background top-up, not a blocker.
    if (!this.isAuthPage) {
      this.profileService.getProfile().subscribe({
        next: (p) => this.timezoneService.setTimezone(p?.timezone ?? null),
        error: () => { /* keep whatever we hydrated from localStorage */ },
      });
    }

    // Note: PrimeNG config can be set up via dependency injection if needed

    if (typeof document !== 'undefined') {
      document.addEventListener('visibilitychange', this.visibilityWake);
      window.addEventListener('pageshow', this.pageshowWake);
    }

    // SCRUM-1943 — raise a clickable toast whenever a report settles ready,
    // regardless of the current page. Tapping it opens the report.
    this.reportReadySub = this.reportGen.settled$.subscribe(({ ticker, ready }) => {
      if (!ready || !ticker) return;
      this.notify.notifyWith({
        type: 'success',
        message: `${ticker} report ready`,
        duration: 8000,
        action: { route: ['/reports/stock', ticker] },
      });
    });
  }

  ngOnDestroy(): void {
    if (typeof document !== 'undefined') {
      document.removeEventListener('visibilitychange', this.visibilityWake);
      window.removeEventListener('pageshow', this.pageshowWake);
    }
    this.reportReadySub?.unsubscribe();
  }
}
